// src/components/Organization/EmailTemplateEditor.tsx

// What? A shared rich-text editor for email template bodies with variable insertion and preview.
// Why? To replace plain Textarea with MMEditor (WYSIWYG) and support cursor-position variable insertion.
// How? Two-column layout (editor + variable picker) with a Preview button that opens a modal
//      rendering the full email (content inside org layout) via the backend preview endpoint.

import React, { useRef, useCallback, useState } from "react";
import { MMEditor, createIconPackPlugin } from "@mmeditor/core";
import "@mmeditor/core/style.css";
import {
  Box,
  Typography,
  Stack,
  Sheet,
  IconButton,
  FormControl,
  FormLabel,
  Input,
  Switch,
  Button,
  Modal,
  ModalDialog,
  DialogTitle,
  CircularProgress,
  Tooltip,
  Alert,
} from "@mui/joy";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import CodeIcon from "@mui/icons-material/Code";
import DataObjectIcon from "@mui/icons-material/DataObject";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import LinkIcon from "@mui/icons-material/Link";
import TableChartIcon from "@mui/icons-material/TableChart";
import FormatClearIcon from "@mui/icons-material/FormatClear";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CodeOffIcon from "@mui/icons-material/CodeOff";
import CloseIcon from "@mui/icons-material/Close";
import { useAuthFetch } from "../../utils/useAuthFetch";

interface TemplateVariable {
  name: string;
  description: string;
}

interface EmailTemplateEditorProps {
  bodyTemplate: string;
  onBodyChange: (html: string) => void;
  variables: TemplateVariable[];
  subjectTemplate: string;
  onSubjectChange: (subject: string) => void;
  organizationId: string;
  templateKey: string;
  // When false, preview / send-style actions are disabled. The template fields
  // themselves remain editable so admins can stage changes while email is off.
  emailEnabled?: boolean;
}

// MUI icon pack for MMEditor toolbar
const muiIconConfig = {
  icons: {
    bold: <FormatBoldIcon fontSize="small" />,
    italic: <FormatItalicIcon fontSize="small" />,
    underline: <FormatUnderlinedIcon fontSize="small" />,
    strike: <StrikethroughSIcon fontSize="small" />,
    code: <CodeIcon fontSize="small" />,
    "code-block": <DataObjectIcon fontSize="small" />,
    bullet: <FormatListBulletedIcon fontSize="small" />,
    number: <FormatListNumberedIcon fontSize="small" />,
    link: <LinkIcon fontSize="small" />,
    table: <TableChartIcon fontSize="small" />,
    clear: <FormatClearIcon fontSize="small" />,
  },
};

const iconPackPlugin = createIconPackPlugin(muiIconConfig);

/**
 * Build sample context data from variable definitions for preview rendering.
 */
function buildSampleContext(
  variables: TemplateVariable[],
): Record<string, string> {
  const ctx: Record<string, string> = {};
  for (const v of variables) {
    ctx[v.name] = `[${v.name}]`;
  }
  return ctx;
}

const EmailTemplateEditor: React.FC<EmailTemplateEditorProps> = ({
  bodyTemplate,
  onBodyChange,
  variables,
  subjectTemplate,
  onSubjectChange,
  organizationId,
  templateKey,
  emailEnabled = true,
}) => {
  const authFetch = useAuthFetch();
  const editorContainerRef = useRef<HTMLDivElement>(null);
  // Default to source mode so Jinja `{% %}` and `{{ }}` blocks survive editing.
  // The rich-text editor's auto-link / auto-format behaviour can mangle Jinja
  // syntax (e.g. rewriting "Source" as an anchor to a URL-encoded tag).
  const [showSource, setShowSource] = useState(true);

  // Preview modal state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewSubject, setPreviewSubject] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");

  const insertVariable = useCallback(
    (varName: string) => {
      const tag = `{{ ${varName} }}`;
      // Try to insert at cursor position in the MMEditor (Quill-based)
      const editorEl =
        editorContainerRef.current?.querySelector<HTMLElement>(".ql-editor");
      if (editorEl) {
        editorEl.focus();
        const inserted = document.execCommand("insertText", false, tag);
        if (inserted) return;
      }
      // Fallback: append to end
      onBodyChange(bodyTemplate + tag);
    },
    [bodyTemplate, onBodyChange],
  );

  const handlePreview = useCallback(async () => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewError("");
    setPreviewHtml("");
    setPreviewSubject("");

    try {
      const data = await authFetch(
        `/api/email/organization/${organizationId}/templates/${templateKey}/preview`,
        "preview email template",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ context: buildSampleContext(variables) }),
        },
      );
      setPreviewSubject(data?.subject || "");
      setPreviewHtml(data?.html_body || "");
    } catch (err) {
      setPreviewError(`Failed to render preview: ${err}`);
    } finally {
      setPreviewLoading(false);
    }
  }, [authFetch, organizationId, templateKey, variables]);

  return (
    <>
      <Stack spacing={2}>
        {/* Subject line */}
        <FormControl>
          <FormLabel>Subject Template</FormLabel>
          <Input
            size="sm"
            value={subjectTemplate}
            onChange={(e) => onSubjectChange(e.target.value)}
            placeholder="Email subject with {{ variables }}"
          />
        </FormControl>

        {/* Jinja syntax notice */}
        <Alert
          color="neutral"
          variant="soft"
          size="sm"
          startDecorator={<InfoOutlinedIcon />}
        >
          This template uses Jinja2 syntax. Editing in source mode is
          recommended to avoid mangling <code>{"{% %}"}</code> and{" "}
          <code>{"{{ }}"}</code> blocks. Use Preview to verify rendering.
        </Alert>

        {/* Toolbar: source toggle + preview button */}
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography level="body-sm" sx={{ fontWeight: 600 }}>
            Body Template
          </Typography>
          <Box
            sx={{ ml: "auto", display: "flex", gap: 1.5, alignItems: "center" }}
          >
            <Stack direction="row" spacing={0.5} alignItems="center">
              <CodeOffIcon sx={{ fontSize: 16, color: "neutral.500" }} />
              <Typography level="body-xs">Source</Typography>
              <Switch
                size="sm"
                checked={showSource}
                onChange={(e) => setShowSource(e.target.checked)}
              />
            </Stack>
            <Tooltip
              title={
                emailEnabled ? "" : "Email is disabled for this organization."
              }
              variant="soft"
              disableHoverListener={emailEnabled}
            >
              <span>
                <Button
                  size="sm"
                  variant="outlined"
                  color="neutral"
                  startDecorator={<VisibilityIcon />}
                  onClick={handlePreview}
                  disabled={!emailEnabled}
                >
                  Preview
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Stack>

        {/* Main editor area: Editor + Variables */}
        <Stack direction="row" spacing={2}>
          {/* Editor column */}
          <Box sx={{ flex: 3, minWidth: 0 }}>
            {!showSource ? (
              <Box
                ref={editorContainerRef}
                sx={{
                  border: "1px solid",
                  borderColor: "neutral.outlinedBorder",
                  borderRadius: "sm",
                  overflow: "hidden",
                  "& .ql-toolbar": {
                    borderBottom: "1px solid",
                    borderColor: "neutral.outlinedBorder",
                  },
                  "& .ql-container": {
                    minHeight: 280,
                    maxHeight: 400,
                    overflow: "auto",
                  },
                  "& .ql-editor": {
                    minHeight: 260,
                    fontSize: "14px",
                    lineHeight: 1.6,
                  },
                }}
              >
                <MMEditor
                  value={bodyTemplate}
                  onChange={onBodyChange}
                  plugins={[iconPackPlugin]}
                  style={{ width: "100%" }}
                  placeholder="Write your email template HTML..."
                />
              </Box>
            ) : (
              <textarea
                value={bodyTemplate}
                onChange={(e) => onBodyChange(e.target.value)}
                style={{
                  width: "100%",
                  minHeight: 300,
                  maxHeight: 400,
                  resize: "vertical",
                  fontFamily: "monospace",
                  fontSize: "13px",
                  lineHeight: "1.5",
                  padding: "12px",
                  border: "1px solid #ccc",
                  borderRadius: "6px",
                  boxSizing: "border-box",
                }}
                spellCheck={false}
              />
            )}
          </Box>

          {/* Variables column */}
          <Box sx={{ flex: 1, minWidth: 180 }}>
            <Typography level="body-sm" sx={{ mb: 1, fontWeight: 600 }}>
              Available Variables
            </Typography>
            <Sheet
              variant="soft"
              sx={{
                borderRadius: "sm",
                p: 1.5,
                maxHeight: 380,
                overflow: "auto",
              }}
            >
              <Stack spacing={0.5}>
                {variables.map((v) => (
                  <Box
                    key={v.name}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      py: 0.5,
                      px: 0.5,
                      cursor: "pointer",
                      borderRadius: "xs",
                      "&:hover": { bgcolor: "background.level2" },
                    }}
                    onClick={() => insertVariable(v.name)}
                  >
                    <IconButton size="sm" variant="plain" color="primary">
                      <ContentCopyIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                    <Box>
                      <Typography
                        level="body-xs"
                        sx={{ fontFamily: "monospace", color: "primary.600" }}
                      >
                        {"{{ " + v.name + " }}"}
                      </Typography>
                      <Typography
                        level="body-xs"
                        sx={{ color: "text.tertiary" }}
                      >
                        {v.description}
                      </Typography>
                    </Box>
                  </Box>
                ))}
                {variables.length === 0 && (
                  <Typography
                    level="body-xs"
                    sx={{ color: "text.tertiary", textAlign: "center", py: 2 }}
                  >
                    No variables available
                  </Typography>
                )}
              </Stack>
            </Sheet>
          </Box>
        </Stack>
      </Stack>

      {/* Preview Modal */}
      <Modal open={previewOpen} onClose={() => setPreviewOpen(false)}>
        <ModalDialog
          sx={{
            width: "min(700px, 90vw)",
            height: "85vh",
            maxHeight: "85vh",
            display: "flex",
            flexDirection: "column",
            p: 0,
            overflow: "hidden",
          }}
        >
          {/* Modal header */}
          <Box
            sx={{
              p: 2,
              borderBottom: "1px solid",
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <DialogTitle sx={{ p: 0, mb: 0.25 }}>Email Preview</DialogTitle>
              {previewSubject && (
                <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                  Subject: {previewSubject}
                </Typography>
              )}
            </Box>
            <IconButton
              size="sm"
              variant="plain"
              onClick={() => setPreviewOpen(false)}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Modal body */}
          <Box sx={{ flex: 1, overflow: "hidden" }}>
            {previewLoading && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <CircularProgress size="md" />
              </Box>
            )}
            {previewError && (
              <Box sx={{ p: 3 }}>
                <Typography level="body-sm" sx={{ color: "danger.600" }}>
                  {previewError}
                </Typography>
              </Box>
            )}
            {!previewLoading && !previewError && previewHtml && (
              <iframe
                title="Email Preview"
                srcDoc={previewHtml}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                  backgroundColor: "#f5f5f5",
                }}
                sandbox="allow-same-origin"
              />
            )}
          </Box>
        </ModalDialog>
      </Modal>
    </>
  );
};

export default EmailTemplateEditor;
